import { TabbedLayoutTopBar } from '@/modules/application/components/TabbedLayoutTopBar';
import { TabbedLayout } from '@/modules/application/layouts/TabbedLayout';
import { LayoutActionsProvider } from '@/providers/layout-actions-provider/LayoutActionsProvider';
import { useState, useEffect, FunctionComponent } from 'react';
import { Outlet, useParams } from 'react-router';
import { locationService } from '../api/locations/location.service';
import { LocationModel } from '../types/LocationModel';
import { BackButton } from '@/components/BackButton';
import NavigationTabs from '@/modules/application/components/NavigationTabs';
import TabbedLayoutTopBarHeader from '@/modules/application/components/TabbedLayoutTopBarHeader';
import { TabbedLayoutTopBarVerticalSeperator } from '@/modules/application/components/TabbedLayoutTopBarVerticalSeperator';
import { Alert, Skeleton, Tooltip } from '@mui/material';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { TabMenuItem } from '@/modules/application/types/MenuItem';
import { useTranslation } from '@/lib';

export const GuestLocationDetailsLayout: FunctionComponent = () => {
  const { locationId } = useParams();
  const locationIdNumber = locationId ? Number.parseInt(locationId) : 0;
  const { t } = useTranslation();

  const [location, setLocation] = useState<LocationModel | undefined>(undefined);

  // Guest users always go back to guest locations
  const backButtonPath = '/app/guest-locations';

  useEffect(() => {
    if (locationId) {
      locationService.getById(Number.parseInt(locationId)).then((location) => {
        if (location.isSuccess) {
          setLocation(location.payload);
        }
      });
    }
  }, [locationId]);

  const tabItems: TabMenuItem[] = [
    {
      activeRouteIds: [`/app/guest-locations/${locationIdNumber}/details`],
      label: t('details'),
      url: `/app/guest-locations/${locationIdNumber}/details`,
    },
    {
      activeRouteIds: [`/app/guest-locations/${locationIdNumber}/movements`],
      label: t('movements'),
      url: `/app/guest-locations/${locationIdNumber}/movements`,
    },
    {
      activeRouteIds: [`/app/guest-locations/${locationIdNumber}/balance`],
      label: t('balances'),
      url: `/app/guest-locations/${locationIdNumber}/balance`,
    },
    {
      activeRouteIds: [`/app/guest-locations/${locationIdNumber}/assets`],
      label: t('assets'),
      url: `/app/guest-locations/${locationIdNumber}/assets`,
    },
  ];

  return (
    <LayoutActionsProvider>
      <TabbedLayout
        topBar={
          <TabbedLayoutTopBar>
            <BackButton pathname={backButtonPath} />
            <TabbedLayoutTopBarHeader icon={null}>
              {location?.dto.name ? `${t('location')} - ${location.dto.name}` : <Skeleton width={200} />}
            </TabbedLayoutTopBarHeader>

            <TabbedLayoutTopBarVerticalSeperator />
            <NavigationTabs tabItems={tabItems} />
          </TabbedLayoutTopBar>
        }
      >
        <TabbedPageLayout>
          <TabbedPageLayoutBody>
            <div className="flex h-full grow flex-col ">
              {location?.dto.isSystem && (
                <Alert severity="info" className="mb-4">
                  {t('system_location_alert')}
                  <Tooltip title={t('system_location_tooltip')}>
                    <span className="font-semibold pl-1 underline decoration-dotted">{t('more_info')}</span>
                  </Tooltip>
                </Alert>
              )}
              <Outlet />
            </div>
          </TabbedPageLayoutBody>
        </TabbedPageLayout>
      </TabbedLayout>
    </LayoutActionsProvider>
  );
};
